import type {
  DevDateRange,
  DevDateTimeRange,
  DevNumberFilter,
  DevPriceRowDto,
  DevPriceRowFilter,
} from '@/api';
import { compareNumericValues } from '@/components/table/table-helpers';
import type { NumericFilterValue } from '@/components/table/table-types';
import { addMinutes } from 'date-fns';

export type FilterOption = { id: string; label: string };

/** A column filter as material-react-table stores it. */
export type ColumnFilter = { id: string; value: unknown };

/** Columns with the shared numeric filter (values, ranges and comparisons). */
export const NUMBER_COLUMNS = ['id', 'lp', 'rrp'] as const;
export type NumberColumn = (typeof NUMBER_COLUMNS)[number];

/** Columns with a number input: the row value must be equal to the input. */
export const EXACT_NUMBER_COLUMNS = [
  'year',
  'priceUnitId',
  'productId',
] as const;
export type ExactNumberColumn = (typeof EXACT_NUMBER_COLUMNS)[number];

export const DATE_COLUMNS = ['validFrom', 'validTo'] as const;
export type DateColumn = (typeof DATE_COLUMNS)[number];

export const PROCESSED_OPTIONS: FilterOption[] = [
  { id: 'true', label: 'Processed' },
  { id: 'false', label: 'Not processed' },
];

const isNumberColumn = (id: string): id is NumberColumn =>
  (NUMBER_COLUMNS as readonly string[]).includes(id);

const isExactNumberColumn = (id: string): id is ExactNumberColumn =>
  (EXACT_NUMBER_COLUMNS as readonly string[]).includes(id);

/**
 * Converts the value of the shared numeric column filter to the API number filter.
 * Returns undefined when the filter has no condition.
 */
export const toNumberFilter = (
  filter: NumericFilterValue | undefined,
): DevNumberFilter | undefined => {
  if (!filter) return undefined;
  switch (filter.mode) {
    case 'values': {
      const values = (filter.selectedValues ?? [])
        .map(Number)
        .filter(Number.isFinite);
      return values.length > 0 ? { values } : undefined;
    }
    case 'betweenInclusive':
    case 'betweenExclusive': {
      if (filter.min === undefined && filter.max === undefined) {
        return undefined;
      }
      const inclusive = filter.mode === 'betweenInclusive';
      return {
        min: filter.min,
        minInclusive: inclusive,
        max: filter.max,
        maxInclusive: inclusive,
      };
    }
    case 'greaterThan':
    case 'greaterThanOrEqual':
      return filter.value === undefined
        ? undefined
        : {
            min: filter.value,
            minInclusive: filter.mode === 'greaterThanOrEqual',
          };
    case 'lessThan':
    case 'lessThanOrEqual':
      return filter.value === undefined
        ? undefined
        : {
            max: filter.value,
            maxInclusive: filter.mode === 'lessThanOrEqual',
          };
  }
};

/** Converts the text of a number input to an exact-value filter. */
export const toExactNumberFilter = (
  text: unknown,
): DevNumberFilter | undefined => {
  if (typeof text !== 'string' || text.trim() === '') return undefined;
  const value = Number(text);
  return Number.isFinite(value) ? { values: [value] } : undefined;
};

/** A date range filter value: from the date pickers. A missing end is open. */
type DateRangeValue = { from?: unknown; to?: unknown };

const toValidDate = (value: unknown): Date | undefined =>
  value instanceof Date && !Number.isNaN(value.getTime()) ? value : undefined;

const getRangeEnds = (value: unknown): [Date | undefined, Date | undefined] => {
  if (typeof value !== 'object' || value === null)
    return [undefined, undefined];
  const { from, to } = value as DateRangeValue;
  return [toValidDate(from), toValidDate(to)];
};

/** Date range from the date picker; both days included. */
const toDateRanges = (value: unknown): DevDateRange[] | undefined => {
  const [from, to] = getRangeEnds(value);
  return from || to ? [{ from, to }] : undefined;
};

/**
 * Time range from the date-time picker. The picker selects minutes, so the end minute is included:
 * the API end is one minute later, and not included.
 */
export const toTimeRanges = (
  value: unknown,
): DevDateTimeRange[] | undefined => {
  const [from, to] = getRangeEnds(value);
  return from || to
    ? [{ from, to: to ? addMinutes(to, 1) : undefined }]
    : undefined;
};

/**
 * Converts the material-react-table column filters of a price row table to the API filter.
 */
export const toPriceRowFilter = (
  columnFilters: readonly ColumnFilter[],
): DevPriceRowFilter => {
  const filter: DevPriceRowFilter = {};
  for (const { id, value } of columnFilters) {
    if (isNumberColumn(id)) {
      filter[id] = toNumberFilter(value as NumericFilterValue | undefined);
    } else if (isExactNumberColumn(id)) {
      filter[id] = toExactNumberFilter(value);
    } else if (id === 'validFrom' || id === 'validTo') {
      filter[id] = toDateRanges(value);
    } else if (id === 'updatedAt') {
      filter.updatedAt = toTimeRanges(value);
    } else if (id === 'isProcessed') {
      const selected = (value as string[] | undefined) ?? [];
      if (selected.length > 0) filter.isProcessed = selected[0] === 'true';
    }
  }
  return filter;
};

/** The number of columns whose filter has a condition. Empty range filters ('', '') do not count. */
export const countActiveFilters = (filter: object): number =>
  Object.values(filter).filter((value) => value !== undefined).length;

/**
 * Filter options for a numeric column. The options come from more than one list of rows (the newest rows
 * and the rows that the current filter returns), so the user can select values that the current filter hides.
 */
export const getColumnOptions = (
  column: NumberColumn,
  rowLists: readonly (readonly DevPriceRowDto[])[],
): FilterOption[] => {
  const ids = new Set<string>();
  for (const rows of rowLists) {
    for (const row of rows) ids.add(String(row[column]));
  }
  return [...ids].sort(compareNumericValues).map((id) => ({ id, label: id }));
};
